import { STATUS_PROGRESS_PCT, normalizeMilestoneStatus } from '../projects/milestoneStatusModel';

/**
 * P-10e (D-82) — the progress bar of a task, in the ⏸ / ✓ windows and in the
 * break / clock-out window. Pure; unit-tested in
 * `src/utils/__tests__/progressModel.test.ts`. The server reads the same rule
 * (projectMetrics `taskProgress`): 🟢 completed = 100; otherwise the saved
 * percent, never below what the status stands for.
 *
 *   - the bar moves in steps of 10%; a status floor (0/25/50/75/100) is
 *     exact — QA-3 #3: "בביצוע" is 25% and "בבדיקה" 75% (not 30 / 80), the
 *     same value as the task page and the portal;
 *   - a status change moves the bar to the status's value (0/25/50/75/100),
 *     unless the bar is already higher;
 *   - "הושלם" always sets 100%;
 *   - dragging to 100% asks "לסמן כהושלמה?".
 */

export const PROGRESS_STEP = 10;

/** The value a status stands for (D-56). */
export function statusFloor(status: string | null | undefined): number {
  return STATUS_PROGRESS_PCT[normalizeMilestoneStatus(status)];
}

/**
 * A bar value: the status floor exactly, or a step of 10 above it; 0..100.
 * QA-3 #3 — the floor is never rounded (25 stays 25, 75 stays 75).
 */
export function snapProgress(value: number, floor = 0): number {
  if (!Number.isFinite(value)) return Math.max(0, Math.min(100, floor));
  const f = Math.max(0, Math.min(100, floor));
  const v = Math.max(0, Math.min(100, value));
  if (v <= f) return f;
  const s = Math.round(v / PROGRESS_STEP) * PROGRESS_STEP;
  return Math.max(f, Math.min(100, s));
}

/**
 * QA-3 #3 — one move of the slider from `prev` to the raw `next`: up to the
 * next step of 10, down to the previous one, never below the floor (so from
 * 25 the bar goes 30, 40, …; and back down to 25).
 */
export function stepProgress(prev: number, next: number, floor = 0): number {
  if (!Number.isFinite(next)) return snapProgress(prev, floor);
  const s = next > prev
    ? Math.ceil(next / PROGRESS_STEP) * PROGRESS_STEP
    : Math.floor(next / PROGRESS_STEP) * PROGRESS_STEP;
  return Math.max(Math.max(0, Math.min(100, floor)), Math.min(100, Math.max(0, s)));
}

/** The bar shown for a task: its saved percent, never below its status; completed = 100. */
export function effectiveProgress(status: string | null | undefined, saved?: number | string | null): number {
  if (normalizeMilestoneStatus(status) === 'completed') return 100;
  const n = saved === null || saved === undefined || saved === '' ? NaN : Number(saved);
  const own = Number.isFinite(n) ? Math.max(0, Math.min(100, n)) : 0;
  return Math.max(own, statusFloor(status));
}

/** The bar after the status changed: up to the status's value, unless already higher; completed = 100. */
export function progressAfterStatus(current: number, status: string | null | undefined): number {
  if (normalizeMilestoneStatus(status) === 'completed') return 100;
  return snapProgress(current, statusFloor(status));
}

/** The bar after a drag: steps of 10, never below the status's value. */
export function progressAfterDrag(value: number, status: string | null | undefined): number {
  return snapProgress(value, statusFloor(status));
}

/** Dragging to 100% on a task that is not completed asks "לסמן כהושלמה?". */
export function asksToComplete(progress: number, status: string | null | undefined): boolean {
  return snapProgress(progress, statusFloor(status)) >= 100 && normalizeMilestoneStatus(status) !== 'completed';
}

/**
 * D-124 (QX-58) — "לסמן כהושלמה?" answered "עדיין לא": the bar goes back to
 * where it was BEFORE the drag to 100% (so 100% is never saved for a task
 * that is not completed).
 */
export function progressOnNotYet(beforeDrag: number, status: string | null | undefined): number {
  return snapProgress(beforeDrag, statusFloor(status));
}

/**
 * What the ⏸ / ✓ / break / clock-out windows save for a task after the
 * employee's changes: a new status (not 🟢 — "הושלם" runs the regular close,
 * `useCloseTicket`, with its checks), and the bar when it moved. Pure.
 */
export function progressSaves(
  before: { status: string | null | undefined; progress: number },
  after: { status: string | null | undefined; progress: number }
): { status: string | null; progress: number | null; complete: boolean } {
  const from = normalizeMilestoneStatus(before.status);
  const to = normalizeMilestoneStatus(after.status);
  const complete = to === 'completed' && from !== 'completed';
  if (to === 'completed') return { status: null, progress: null, complete };
  const was = snapProgress(before.progress, statusFloor(before.status));
  const now = snapProgress(after.progress, statusFloor(after.status));
  return {
    status: to !== from ? to : null,
    progress: now !== was ? now : null,
    complete: false,
  };
}
